import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Check, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useWorkingModel } from "@/hooks/useWorkingModel";
import { PageHeader } from "@/components/PageParts";
import PlanSVG from "@/components/PlanSVG";
import { Button, Card, Spinner, Empty } from "@/components/ui";

export default function Variants() {
  const { lang } = useAuth();
  const isAr = lang === "ar";
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [, setWm] = useWorkingModel();

  const kind = params.get("kind") || "villa";
  const plotW = parseFloat(params.get("plot_w") || "18");
  const plotH = parseFloat(params.get("plot_h") || "22");
  const floors = parseInt(params.get("floors") || "1", 10);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [variants, setVariants] = useState([]);
  const [pick, setPick] = useState(null);

  async function generate() {
    setLoading(true);
    setErr(null);
    try {
      const body = { kind, plot_w: plotW, plot_h: plotH, n: 10, floors };
      ["bedrooms", "bathrooms", "num_offices", "num_shops", "num_apartments", "bedrooms_per_unit"].forEach((k) => {
        const v = params.get(k);
        if (v != null && v !== "") body[k] = parseInt(v, 10);
      });
      const langParam = params.get("lang");
      if (langParam) body.lang = langParam;
      ["has_pool", "has_garden", "has_courtyard", "has_garage", "has_master_ensuite", "has_balconies", "has_maid_room", "has_office", "has_basement", "has_front_shops"].forEach((k) => {
        if (params.get(k) === "true") body[k] = true;
      });
      const neighborParam = params.get("neighbor_side");
      if (neighborParam) body.neighbor_side = neighborParam;
      const res = await api.editorGenerateVariants(body);
      setVariants(res.variants || []);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { generate(); /* eslint-disable-next-line */ }, [kind, plotW, plotH, floors]);

  function choose(v) {
    setPick(v.index);
    setWm({
      model: v.model, kind, floors, slab_system: "hordi_regular",
      has_basement: !!(v.model?.building_meta?.has_basement),
      basement_depth_m: v.model?.building_meta?.basement_depth_m || 3.0,
      generation_params: { plot_w: plotW, plot_h: plotH, variant_key: v.key },
      title: isAr ? v.name_ar : v.name_en,
    });
    toast.success(isAr ? "تم اختيار المخطط" : "Plan selected");
    navigate("/editor");
  }

  if (loading)
    return (
      <div className="grid min-h-[70vh] place-items-center gap-3 text-center">
        <Spinner size={40} className="text-accent" />
        <p className="text-sm text-muted-foreground">{isAr ? "يولّد ١٠ مخططات معمارية…" : "Generating 10 architectural variants…"}</p>
        <p className="text-xs text-accent">{`${plotW}×${plotH} m · ${kind}`}</p>
      </div>
    );

  return (
    <div className="mx-auto max-w-7xl px-4 py-6">
      <PageHeader
        eyebrow={isAr ? "المخططات" : "Variants"}
        title={isAr ? "١٠ مخططات معمارية للاختيار" : "10 architectural variants"}
        subtitle={`${plotW}×${plotH} m · ${kind} · ${isAr ? "اختر واحداً لبناء الأساسات والكهرباء والسباكة عليه" : "pick one to build foundation / MEP / schedules on"}`}
        right={<Button variant="outline" onClick={generate}><RefreshCw size={16} /> {isAr ? "إعادة التوليد" : "Regenerate"}</Button>}
      />
      {err || variants.length === 0 ? (
        <Empty icon={Sparkles} title={isAr ? "لم يتم توليد أي مخطط" : "No variants generated"} hint={err || (isAr ? "جرّب أبعاداً أكبر" : "Try larger dimensions")} action={<Button onClick={generate}>{isAr ? "أعد المحاولة" : "Retry"}</Button>} />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {variants.map((v) => (
            <Card key={v.key} className="flex flex-col gap-3 p-0 overflow-hidden">
              <div className="flex items-center gap-2 bg-primary px-3 py-2 text-primary-foreground">
                <span className="text-xs font-black text-amber-300">{v.key}</span>
                <span className="flex-1 truncate text-sm font-black">{isAr ? v.name_ar : v.name_en}</span>
              </div>
              <div className="mx-3 overflow-hidden rounded-lg border border-border bg-white">
                <PlanSVG model={v.model} w={360} h={260} showLabels={false} showPlot={false} />
              </div>
              <div className="flex flex-col gap-2 px-3 pb-3">
                <p className="line-clamp-2 min-h-[32px] text-xs text-muted-foreground">{isAr ? v.desc_ar : v.desc_en}</p>
                <div className="flex flex-wrap gap-1">
                  {(v.features || []).slice(0, 5).map((f, i) => (
                    <span key={i} className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold text-emerald-400">{f}</span>
                  ))}
                </div>
                <div className="flex justify-between text-xs font-bold text-foreground/80">
                  <span>🧱 {v.metrics?.wall_count}</span>
                  <span>🚪 {v.metrics?.opening_count}</span>
                  <span>🏛️ {v.metrics?.column_count}</span>
                  <span>🏠 {v.metrics?.room_count}</span>
                </div>
                <Button variant="accent" size="sm" className="mt-1" loading={pick === v.index} onClick={() => choose(v)} data-testid={`variant-${v.key}`}>
                  <Check size={14} /> {isAr ? "اختر هذا المخطط" : "Choose this plan"}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
